// safety/safety-walks/pages/SafetyWalksRegisterPage.tsx
// Airtable-style register: search, status filter (Draft/In Progress/Completed),
// walk number / project / location / date / conductor / observation counts /
// status / last updated. Mobile: table collapses to cards (Miro: mobile critical).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import AccessRestricted from '../../../../components/AccessRestricted';
import { listWalks } from '../../../../utils/safety-walks-api';
import { StatusPill, fmtDate, initials } from '../components/shared';
import type { SafetyWalk, SafetyWalkStatus } from '../../../../types/safety-walk.types';

const STATUSES: ('All' | SafetyWalkStatus)[] = ['All', 'Draft', 'In Progress', 'Completed'];
const PAGE_SIZE = 20;

export const SafetyWalksRegisterPage: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    const [walks, setWalks] = useState<SafetyWalk[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState<'All' | SafetyWalkStatus>('All');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const canView = ability.can(ACTIONS.READ, RESOURCES.SAFETY_WALK_LIST);
    const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.SAFETY_WALK);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => setPage(1), [filter, debouncedSearch]);

    const fetchWalks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listWalks({
                status: filter === 'All' ? undefined : filter,
                search: debouncedSearch || undefined,
                page,
                limit: PAGE_SIZE,
                sortBy: 'updatedAt',
                sortDir: 'desc',
            });
            setWalks(res.walks || []);
            setTotal(res.total || 0);
        } catch {
            // api util toasts
        } finally {
            setLoading(false);
        }
    }, [filter, debouncedSearch, page]);

    useEffect(() => {
        if (canView) fetchWalks();
    }, [fetchWalks, canView]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);

    const openWalk = useCallback((id: number) => navigate(`/safety/walks/${id}`), [navigate]);

    const body = useMemo(() => {
        if (!canView) {
            return <AccessRestricted accessMessage="You don't have permission to access the Safety Walk Register." />;
        }

        return (
            <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1500px] mx-auto w-full">
                <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
                    <div>
                        <button
                            onClick={() => navigate('/safety/walks')}
                            className="text-sm text-primary hover:underline mb-1 inline-flex items-center gap-1"
                        >
                            <ChevronLeft size={14} /> Safety Walk Dashboard
                        </button>
                        <h1 className="text-2xl font-medium">Safety Walk Register</h1>
                        <p className="text-sm text-muted-foreground mt-1">{total} walk(s) recorded</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {canCreate && (
                            <button
                                onClick={() => navigate('/safety/walks/new')}
                                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                            >
                                <Plus size={16} />
                                New Safety Walk
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search walks..."
                                className="w-64 h-10 pl-9 pr-3 rounded-lg border border-border bg-card text-sm"
                            />
                        </div>
                        <div className="flex items-center gap-1 border border-border rounded-lg p-1 bg-card">
                            {STATUSES.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setFilter(s)}
                                    className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                        filter === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 size={24} className="animate-spin text-muted-foreground" />
                    </div>
                ) : walks.length === 0 ? (
                    // Miro "NO CTA's" — empty state without action buttons
                    <div className="rounded-xl border border-border bg-card py-16 text-center">
                        <p className="text-muted-foreground">No safety walks found</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block rounded-xl border border-border bg-card overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                                        <th className="px-4 py-3 font-medium">Walk Number</th>
                                        <th className="px-4 py-3 font-medium">Project</th>
                                        <th className="px-4 py-3 font-medium">Location</th>
                                        <th className="px-4 py-3 font-medium">Walk Date</th>
                                        <th className="px-4 py-3 font-medium">Conducted By</th>
                                        <th className="px-4 py-3 font-medium text-right">Observations</th>
                                        <th className="px-4 py-3 font-medium text-right">Open</th>
                                        <th className="px-4 py-3 font-medium text-right">Closed</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium">Last Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {walks.map((w) => (
                                        <tr
                                            key={w.id}
                                            onClick={() => openWalk(w.id)}
                                            className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer"
                                        >
                                            <td className="px-4 py-3 whitespace-nowrap text-primary font-medium">{w.walkNumber}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">{w.project || '–'}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{w.location || '–'}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(w.walkDate)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="h-6 w-6 rounded-full bg-muted text-muted-foreground text-[10px] font-medium flex items-center justify-center">
                                                        {initials(w.conductedBy)}
                                                    </span>
                                                    {w.conductedBy || '–'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right font-medium">{w.observationCounts?.total ?? 0}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right text-amber-600 font-medium">{w.observationCounts?.open ?? 0}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right text-emerald-600 font-medium">{w.observationCounts?.closed ?? 0}</td>
                                            <td className="px-4 py-3 whitespace-nowrap"><StatusPill status={w.status} /></td>
                                            <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(w.updatedAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards */}
                        <div className="md:hidden space-y-3">
                            {walks.map((w) => (
                                <button
                                    key={w.id}
                                    onClick={() => openWalk(w.id)}
                                    className="w-full text-left rounded-xl border border-border bg-card p-4 hover:bg-accent/40"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-primary font-medium">{w.walkNumber}</span>
                                        <StatusPill status={w.status} />
                                    </div>
                                    <p className="text-sm font-medium">{w.project || '–'}</p>
                                    <p className="text-xs text-muted-foreground">{w.location || '–'}</p>
                                    <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                                        <span>{fmtDate(w.walkDate)} • {w.conductedBy || '–'}</span>
                                        <span className="flex items-center gap-2">
                                            <span className="font-medium text-foreground">{w.observationCounts?.total ?? 0} obs</span>
                                            <span className="text-amber-600 font-medium">{w.observationCounts?.open ?? 0} open</span>
                                            <span className="text-emerald-600 font-medium">{w.observationCounts?.closed ?? 0} closed</span>
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                            <span>Showing {from}–{to} of {total}</span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    className="h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-accent disabled:opacity-40"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span>{page} / {totalPages}</span>
                                <button
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                    className="h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-accent disabled:opacity-40"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }, [canView, walks, total, page, filter, search, loading, openWalk, navigate, canCreate, fetchWalks]);
    return body;
};
